import { useState } from 'react';
import { StatusPill } from '@/components/common/StatusPill';
import { BatchAssignmentPlanner } from '@/features/management-dashboard/components/BatchAssignmentPlanner';
import { ClientExplorerSection } from '@/features/management-dashboard/components/ClientExplorerSection';
import { ManagementDashboardHeader } from '@/features/management-dashboard/components/ManagementDashboardHeader';
import { ManagementDashboardTabs } from '@/features/management-dashboard/components/ManagementDashboardTabs';
import { OverviewDashboardSection } from '@/features/management-dashboard/components/OverviewDashboardSection';
import { UserExplorerSection } from '@/features/management-dashboard/components/UserExplorerSection';
import { ProjectReportSection } from '@/features/reports/components/ProjectReportSection';
import { useManagementDashboardData } from '@/features/management-dashboard/useManagementDashboardData';
import { exportReportForWeb } from '@/features/reports/api';

export function ManagementDashboardPage() {
  const {
    currentUser,
    loading,
    error, clearError,
    range,
    setRange,
    rangeOptions,
    activeTab,
    setActiveTab,
    kpis,
    users,
    clients,
    tickets,
    fms,
    todo,
    invoices,
    attendance,
    selectedUserId,
    setSelectedUserId,
    selectedUser,
    userExplorer,
    selectedClientId,
    setSelectedClientId,
    selectedClient,
    clientExplorer,
    clientTicketStats,
    selectedProject,
    setSelectedProject,
    refresh,
    bounds,
    startDate,
    endDate,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd
  } = useManagementDashboardData();

  const [exportingFormat, setExportingFormat] = useState(null);
  const [exportMsg, setExportMsg] = useState(null);

  const handleExport = async (format) => {
    setExportingFormat(format);
    setExportMsg(null);
    try {
      let sheetName = 'Management_Report';
      let filters = { scope: 'management' };

      if (activeTab === 'project-report') {
        sheetName = 'Project_Report';
        filters = { scope: 'project', selectedProject };
      } else if (activeTab === 'user-explorer') {
        sheetName = 'User_Explorer_Report';
        filters = { scope: 'user-explorer', userId: selectedUserId, userName: selectedUser?.['Employee Name'] || selectedUser?.name };
      } else if (activeTab === 'client-explorer') {
        sheetName = 'Client_Explorer_Report';
        filters = { scope: 'client-explorer', clientId: selectedClientId, clientName: selectedClient?.['Client Name'] || selectedClient?.name };
      }

      const employeeId = currentUser?.['Employee ID'] || '';
      const role = currentUser?.Role || 'Admin';
      const activeStart = bounds?.startDate || startDate || customStart || '';
      const activeEnd = bounds?.endDate || endDate || customEnd || '';

      const payload = await exportReportForWeb(
        format,
        sheetName,
        employeeId,
        role,
        activeStart,
        activeEnd,
        filters
      );

      if (!payload?.success || !payload?.base64Data) {
        throw new Error(payload?.message || 'Report export failed.');
      }

      if (typeof window !== 'undefined') {
        const binaryString = window.atob(payload.base64Data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const mimeType = format === 'pdf' ? 'application/pdf' : format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
        const blob = new Blob([bytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = payload.fileName || `management-report.${format}`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }

      setExportMsg({
        tone: 'success',
        text: `${sheetName.replace(/_/g, ' ')} exported successfully (${format.toUpperCase()}).`
      });
    } catch (err) {
      setExportMsg({ tone: 'danger', text: err.message || 'Export failed.' });
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <section className="page-card">
      <ManagementDashboardHeader
        currentUser={currentUser}
        range={range}
        rangeOptions={rangeOptions}
        onRangeChange={setRange}
        onRefresh={refresh}
        customStart={customStart}
        onCustomStartChange={setCustomStart}
        customEnd={customEnd}
        onCustomEndChange={setCustomEnd}
        onExport={handleExport}
        exporting={Boolean(exportingFormat)}
        exportingFormat={exportingFormat}
      />

      {error ? (
        <div className="dashboard-banner dashboard-banner--error">
          <span>{error}</span>
          <button type="button" className="dashboard-banner__close" onClick={clearError}>OK</button>
        </div>
      ) : null}

      {exportMsg ? (
        <div className={`dashboard-banner${exportMsg.tone === 'danger' ? ' dashboard-banner--error' : ''}`} style={{ marginBottom: '12px' }}>
          <span>{exportMsg.text}</span>
          <button type="button" className="dashboard-banner__close" onClick={() => setExportMsg(null)}>OK</button>
        </div>
      ) : null}

      <ManagementDashboardTabs activeTab={activeTab} onTabChange={setActiveTab} userRole={currentUser?.Role || currentUser?.role} />
 
      {activeTab !== 'batch-planner' ? (
        <div className="management-dashboard-content-zoom" style={{ zoom: 1.1, width: '100%' }}>
          {activeTab === 'overview' ? (
            <OverviewDashboardSection
              kpis={kpis}
              tickets={tickets}
              fms={fms}
              todo={todo}
              users={users}
              clients={clients}
              invoices={invoices}
              attendance={attendance}
              onOpenBatchModal={(type) => setActiveTab('batch-planner')}
            />
          ) : null}

          {activeTab === 'user-explorer' ? (
            <UserExplorerSection
              users={users}
              selectedUserId={selectedUserId}
              onUserChange={setSelectedUserId}
              selectedUser={selectedUser}
              explorer={userExplorer}
            />
          ) : null}

          {activeTab === 'client-explorer' && !/^(team leader|tl)$/i.test(String(currentUser?.Role || currentUser?.role || '').trim()) ? (
            <ClientExplorerSection
              clients={clients}
              selectedClientId={selectedClientId}
              onClientChange={setSelectedClientId}
              selectedClient={selectedClient}
              explorer={clientExplorer}
              tickets={tickets}
              fms={fms}
              todo={todo}
              invoices={invoices}
              clientTicketStats={clientTicketStats}
              currentUser={currentUser}
            />
          ) : null}

          {activeTab === 'project-report' ? (
            <ProjectReportSection
              tickets={tickets}
              selectedProject={selectedProject}
              onProjectChange={setSelectedProject}
              customStart={customStart}
              onCustomStartChange={setCustomStart}
              customEnd={customEnd}
              onCustomEndChange={setCustomEnd}
            />
          ) : null}
        </div>
      ) : null}

      {activeTab === 'batch-planner' ? (
        <BatchAssignmentPlanner
          users={users}
          clients={clients}
          currentUser={currentUser}
          onCompleted={refresh}
        />
      ) : null}
    </section>
  );
}
